import classNames from 'classnames'
import { Eye, Trash2, TriangleAlert, X } from 'lucide-react'
import { useMemo, useState } from 'react'
import { createPortal } from 'react-dom'

import Button from '@/elements/Button'
import Flex, { type SpacersRangeType } from '@/elements/Flex'
import Text from '@/elements/Text'
import Modal from '@/components/Modal'
import Timestamp from '@/elements/Timestamp'
import { checkImageStatus } from '@/utils/individualHeadshots'
import { colors } from '@/utils/theme'
import { type IndividualImageSearchRefinementResponse } from '@/api/types/Organization'

import './ImageGrid.scss'

type ComplexImageType = Omit<IndividualImageSearchRefinementResponse,
  'metadata' | 'subject' | 'createdBy' | 'source' | 'info'
> & {
  metadata?: IndividualImageSearchRefinementResponse['metadata']
  subject?: IndividualImageSearchRefinementResponse['subject']
  createdBy?: IndividualImageSearchRefinementResponse['createdBy']
  source?: IndividualImageSearchRefinementResponse['source']
  info?: IndividualImageSearchRefinementResponse['info']
}

interface ImageGridProps {
  images: ComplexImageType[] | string[]
  labels?: string[]
  imageGap?: SpacersRangeType
  onImageRemove?: (externalId: string) => void
}

const isObjectImage = (
  img: ComplexImageType | string
): img is ComplexImageType => {
  return typeof img === 'object' && img !== null && 'url' in img
}

const normalizeImage = (
  img: ComplexImageType | string
): ComplexImageType | string => {
  if (isObjectImage(img)) return img

  const url = img

  return {
    id: url,
    externalId: url,
    url,
  }
}

function ImageGrid({
  images,
  labels,
  imageGap = 3,
  onImageRemove,
}: ImageGridProps) {
  const [imageToDelete, setImageToDelete] = useState<string | null>(null)
  const [imageToPreview, setImageToPreview] =
    useState<IndividualImageSearchRefinementResponse | null>(null)

  const hasSimpleImages = useMemo(
    () => images.some(img => !isObjectImage(img)),
    [images]
  )

  const handleDelete = () => {
    if (imageToDelete !== null && onImageRemove) {
      onImageRemove(imageToDelete)
    }
    setImageToDelete(null)
  }

  const renderFileName = (fileName: string) => {
    const parts = fileName.split('.')
    if (parts.length > 1) {
      const extension = parts.pop()
      const name = parts.join('.')
      return (
        <Flex inline wfull>
          <Text variant='textblock' color='black-light-40' className='image-grid-filename'>
            {name}
          </Text>
          <Text variant='textblock' color='black-light-40' style={{ overflowWrap: 'break-word' }}>
            .{extension}
          </Text>
        </Flex>
      )
    }
    return (
      <Text variant='textblock' color='black-light-40' className='image-grid-filename'>
        {fileName}
      </Text>
    )
  }

  return (
    <>
      <Flex
        wfull
        gap={imageGap}
        grid
        grow
        cols='repeat(auto-fill, minmax(150px, 1fr))'
        className='image-grid'
      >
        {images.map((raw, index) => {
          const image = normalizeImage(raw) as IndividualImageSearchRefinementResponse
          const imageStatus = hasSimpleImages ? null : checkImageStatus(image)

          return (
            <Flex key={image.id} gap={1} style={{ direction: 'ltr' }}>
              <Flex
                className={classNames('image-grid-placeholder', {
                  'image-grid-error': imageStatus?.hasError,
                })}
              >
                <img
                  src={image.url}
                  alt={image.metadata?.fileName || `Image ${image.id}`}
                  className='image-grid-image'
                />
                <Flex
                  inline
                  wfull
                  hfull
                  align='center'
                  justify='center'
                  className='image-grid-overlay'
                  onClick={() => !onImageRemove && setImageToPreview(image)}
                  style={{ cursor: !onImageRemove ? 'pointer' : 'default' }}
                  gap={1}
                >
                  {onImageRemove && (
                    <>
                      <Button
                        variant='iconBtn'
                        small
                        styles={{ border: 'none' }}
                        onClick={() => setImageToPreview(image)}
                      >
                        <Eye size={18} />
                      </Button>
                      <Button
                        variant='iconDangerBtn'
                        small
                        onClick={() => setImageToDelete(image.externalId)}
                      >
                        <Trash2 size={18} />
                      </Button>
                    </>
                  )}
                </Flex>
              </Flex>

              <Flex wfull>
                {hasSimpleImages && labels?.length && !!labels[index] && renderFileName(labels[index])}
                {!hasSimpleImages && image.metadata?.fileName && renderFileName(image.metadata?.fileName)}
                {!hasSimpleImages && imageStatus?.hasError && (
                  <Flex inline gap={1}>
                    <TriangleAlert
                      size={16}
                      style={{ minWidth: 16, marginTop: 3 }}
                      color={colors['red']}
                    />
                    <Text color='red' variant='textblock'>
                      {imageStatus.errorMessage}
                    </Text>
                  </Flex>
                )}
              </Flex>
            </Flex>
          )
        })}

        <Modal
          isOpen={imageToDelete !== null}
          title='Remove Image'
          onSave={handleDelete}
          onCancel={() => setImageToDelete(null)}
          cancelLabel='No'
          saveLabel='Yes'
        >
          <Text color='black' style={{ hyphens: 'none' }}>
            Are you sure you want to remove this image? This action cannot be undone.
          </Text>
        </Modal>
      </Flex>

      {imageToPreview &&
        createPortal(
          <div
            className='image-preview-overlay'
            onClick={() => setImageToPreview(null)}
          >
            <div
              onClick={e => e.stopPropagation()}
              className={classNames('card image-preview-container', {
                'image-preview-error':
                  !hasSimpleImages && checkImageStatus(imageToPreview).hasError,
              })}
            >
              <Flex className='image-preview-body'>
                <div className='image-preview-wrapper'>
                  <img
                    src={imageToPreview.url}
                    alt={imageToPreview.metadata?.fileName || 'Image to preview'}
                  />
                </div>
                <Button variant='iconBtn' small onClick={() => setImageToPreview(null)}>
                  <X />
                </Button>
              </Flex>

              <Flex justify='space-between' className='image-preview-footer' gap={1}>
                {!hasSimpleImages && (
                  <>
                    {imageToPreview.createdAt && (
                      <Timestamp from={imageToPreview.createdAt ?? ''} noIcon />
                    )}
                    {imageToPreview.metadata?.fileName && (
                      <Text color='black' style={{ maxWidth: 600, overflowWrap: 'anywhere' }}>
                        {imageToPreview.metadata.fileName}
                      </Text>
                    )}
                    {checkImageStatus(imageToPreview).hasError ? (
                      <Flex inline align='center' gap={1}>
                        <TriangleAlert size={18} color={colors['red']} />
                        <Text color='red'>{checkImageStatus(imageToPreview).errorMessage}</Text>
                      </Flex>
                    ) : null}
                  </>
                )}
              </Flex>
            </div>
          </div>,
          document.body
        )}
    </>
  )
}

export default ImageGrid
